import React, { useState, useEffect, useMemo, useRef } from 'react';
import { warehouseService } from '../../_services/warehouse.service';
import { Breadcrumb, PageHeader, Spin, AutoComplete } from 'antd';
import Page from '_components/Page';
import { stockClassificationService } from '../../_services/stockclassification.service';
import { materialTypeService } from '../../_services/materialttype.service';

// Update colorMap for dark mode
const colorMap = {
  AVAILABLE: '#1976d2', // blue
  EMPTY: '#23263a',    // dark gray
  FULL: '#43a047',     // green
  WARNING: '#ff9800',  // orange
  DEFAULT: '#181c2a',  // background
};

// Centralized color constants for bins and legend
const BIN_COLORS = {
  HIGH:   { label: 'High Stock (≥80%)',   color: 'RED' }, // green
  MEDIUM: { label: 'Medium Stock (≥50%)', color: 'ORANGE' }, // orange
  LOW:    { label: 'Low Stock (under 50%)', color: 'YELLOW' }, // dark bg
  EMPTY:  { label: 'Empty',                color: 'GREEN' }, // dark gray
};

// Get bin color based on stock quantity
function getBinColor(bin) {
  if (typeof bin.availableStockQuantity === 'number' && typeof bin.allowedStockQuantity === 'number') {
    if (bin.availableStockQuantity === 0) return BIN_COLORS.EMPTY.color;
    if (bin.availableStockQuantity >= bin.allowedStockQuantity) return BIN_COLORS.HIGH.color;
    if (bin.availableStockQuantity / bin.allowedStockQuantity >= 0.5) return BIN_COLORS.MEDIUM.color;
    return BIN_COLORS.LOW.color;
  }
  return BIN_COLORS.LOW.color;
}

// Legend items array for easy mapping
const BIN_LEGEND = [
  BIN_COLORS.HIGH,
  BIN_COLORS.MEDIUM,
  BIN_COLORS.LOW,
  BIN_COLORS.EMPTY,
];

function getRackColor(rack) {
  if (!rack.stockBins || rack.stockBins.length === 0) return '#23263a'; // empty/dark
  // Priority: EMPTY > LOW > MEDIUM > HIGH
  let hasEmpty = false, hasLow = false, hasMedium = false, hasHigh = false;
  for (const bin of rack.stockBins) {
    const color = getBinColor(bin);
    if (color === BIN_COLORS.EMPTY.color) hasEmpty = true;
    else if (color === BIN_COLORS.LOW.color) hasLow = true;
    else if (color === BIN_COLORS.MEDIUM.color) hasMedium = true;
    else if (color === BIN_COLORS.HIGH.color) hasHigh = true;
  }
  if (hasEmpty) return BIN_COLORS.EMPTY.color;
  if (hasLow) return BIN_COLORS.LOW.color;
  if (hasMedium) return BIN_COLORS.MEDIUM.color;
  return BIN_COLORS.HIGH.color;
}

const StoreView2D = ({ onViewChange }) => {
  // --- Warehouse Suggestions ---
  const [warehouses, setWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [racks, setRacks] = useState([]);
  const [bins, setBins] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedRack, setSelectedRack] = useState('');
  const [selectedBin, setSelectedBin] = useState('');
  const [loadingRacks, setLoadingRacks] = useState(false);

  // --- Pending (UI) State for Search ---
  const [pendingWarehouse, setPendingWarehouse] = useState('');
  const [pendingRack, setPendingRack] = useState('');
  const [pendingBin, setPendingBin] = useState('');

  // --- Bin Details Modal State ---
  const [binModalOpen, setBinModalOpen] = useState(false);
  const [binModalData, setBinModalData] = useState(null);
  const [binModalPage, setBinModalPage] = useState(1);
  const [binModalPageSize] = useState(50);
  const [binModalPagination, setBinModalPagination] = useState(null);
  const [currentBinId, setCurrentBinId] = useState(null);
  const [loadingBinDetails, setLoadingBinDetails] = useState(false);

  // --- Data ---
  const [warehouseDetails, setWarehouseDetails] = useState(null);
  const [loadingWarehouseDetails, setLoadingWarehouseDetails] = useState(false);
  const [searchError, setSearchError] = useState('');

  // --- Stock Category & Material Filters ---
  const [stockCategories, setStockCategories] = useState([]);
  const [pendingStockCategory, setPendingStockCategory] = useState('');
  const [materialTypes, setMaterialTypes] = useState([]);
  const [pendingMaterialType, setPendingMaterialType] = useState('');

  // Fetch warehouse suggestions on mount
  useEffect(() => {
    setLoadingWarehouses(true);
    warehouseService.getSuggestion().then(res => {
      setWarehouses(res.content || []);
      setLoadingWarehouses(false);
    }).catch(() => setLoadingWarehouses(false));
  }, []);

  // Fetch stock categories and material types on mount
  useEffect(() => {
    stockClassificationService.getSuggestion().then(res => {
      setStockCategories(res.content || []);
    });
    materialTypeService.getSuggestion().then(res => {
      setMaterialTypes(res.content || []);
    });
  }, []);

  // Fetch racks when warehouse changes
  useEffect(() => {
    if (pendingWarehouse) {
      setLoadingRacks(true);
      warehouseService.getRacksByWarehouseId(pendingWarehouse).then(res => {
        setRacks(res.content?.racks || []);
        setPendingRack('');
        setBins([]);
        setPendingBin('');
        setLoadingRacks(false);
      }).catch(() => setLoadingRacks(false));
    } else {
      setRacks([]);
      setBins([]);
      setPendingRack('');
      setPendingBin('');
      setLoadingRacks(false);
    }
  }, [pendingWarehouse]);

  // Update bins when rack changes
  useEffect(() => {
    if (pendingRack) {
      const rack = racks.find(r => r.id === pendingRack);
      setBins(rack?.stockBins || []);
      setPendingBin('');
    } else {
      setBins([]);
      setPendingBin('');
    }
  }, [pendingRack, racks]);

  // --- Data ---
  const allWarehouses = warehouses; // This is now fetched from API
  // Filter racks by selected warehouse
  const filteredRacks = useMemo(() => {
    let racksArr = racks;
    if (selectedRack) {
      const rack = racksArr.find(r => r.id === selectedRack);
      if (!rack) return [];
      // If bin selected, filter bins
      if (selectedBin) {
        return [{ ...rack, stockBins: rack.stockBins.filter(b => b.id === selectedBin) }];
      }
      return [rack];
    }
    return racksArr;
  }, [racks, selectedRack, selectedBin]);

  // Optionally, insert pathway rows after every N racks, or based on rack names
  // For demo, after every 2 racks
  const rows = [];
  filteredRacks.forEach((rack, idx) => {
    rows.push({ type: 'rack', rack });
    if ((idx + 1) % 2 === 0 && idx !== filteredRacks.length - 1) {
      rows.push({ type: 'pathway' });
    }
  });

  // --- Search Button Handler ---
  const handleSearch = async () => {
    setSelectedWarehouse(pendingWarehouse);
    setSelectedRack(pendingRack);
    setSelectedBin(pendingBin);
    setLoadingWarehouseDetails(true);
    setSearchError('');
    try {
      const data = await warehouseService.fetchWarehouseData(
        pendingWarehouse || undefined,
        pendingRack || undefined,
        pendingBin || undefined,
        pendingStockCategory || undefined,
        pendingMaterialType || undefined
      );
      if (data.content && data.content[0]) {
        setWarehouseDetails(data.content[0]);
      } else {
        setWarehouseDetails(null);
        setSearchError('No data found');
      }
    } catch (e) {
      setWarehouseDetails(null);
      setSearchError('Failed to fetch warehouse data');
    }
    setLoadingWarehouseDetails(false);
  };

  // --- Clear Filters ---
  const handleClear = () => {
    setPendingWarehouse('');
    setPendingRack('');
    setPendingBin('');
    setSelectedWarehouse('');
    setSelectedRack('');
    setSelectedBin('');
  };

  // Reset rack/bin when warehouse changes (pending)
  useEffect(() => {
    setPendingRack('');
    setPendingBin('');
  }, [pendingWarehouse]);

  // --- Keyboard: Search on Enter ---
  const filterPanelRef = useRef();
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Enter') {
        if (filterPanelRef.current && filterPanelRef.current.contains(document.activeElement)) {
          handleSearch();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // --- Active Filter Summary ---
  const activeWarehouse = warehouses.find(w => w.id === selectedWarehouse);
  const activeRack = racks.find(r => r.id === selectedRack);
  const activeBin = activeRack && activeRack.stockBins.find(b => b.id === selectedBin);

  // Modal Table Pagination Logic
  let tableRows = [];
  if (binModalData && Array.isArray(binModalData.content)) {
    tableRows = binModalData.content;
  } else if (binModalData && binModalData.content) {
    tableRows = [binModalData.content];
  } else if (Array.isArray(binModalData)) {
    tableRows = binModalData;
  } else if (binModalData) {
    tableRows = [binModalData];
  }

  // Use a single displayKeys array for both header and body
  const excludeKeys = ['id', 'status', 'tempNumber', 'grossWeight', 'netWeight'];
  const displayKeys = tableRows.length > 0
    ? Object.keys(tableRows[0]).filter(key => !excludeKeys.includes(key))
    : [];

  // Fetch bin details with pagination
  const fetchBinDetails = async (binId, pageNumber = 1, pageSize = 50) => {
    try {
      const data = await warehouseService.getBinDetails(binId, pageNumber, pageSize, 'AVAILABLE');
      setBinModalData(data);
      setBinModalPagination(data.pagination);
      setBinModalPage(pageNumber);
      setCurrentBinId(binId);
      setLoadingBinDetails(false);
    } catch (e) {
      setLoadingBinDetails(false);
      alert('Failed to fetch bin details');
    }
  };

  // Add a mapping for user-friendly column headers
  const binDetailHeaderMap = {
    bin: 'Bin',
    days: 'Aging',
    grossWeight: 'Gross Weight',
    id: 'ID',
    materialType: 'Material Type',
    mode: 'Mode',
    netWeight: 'Net Weight',
    palletNo: 'Pallet No',
    productionDate: 'Production Date',
    quantity: 'Quantity',
    rackCode: 'Rack Code',
    receiptNumber: 'Receipt Number',
    receivedDate: 'Received Date',
    status: 'Status',
    supplier: 'Supplier',
    tempNumber: 'Temp Number',
  };
  function getBinDetailHeader(key) {
    return binDetailHeaderMap[key] || (key.charAt(0).toUpperCase() + key.slice(1));
  }

  return (
    <PageHeader
      title={
        <Breadcrumb style={{ marginLeft: 20 }}>
          <Breadcrumb.Item>
            <a href="/dashboard">Dashboard</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Store Location View</Breadcrumb.Item>
        </Breadcrumb>
      }
      style={{
        background: '#10162a',
        borderRadius: 0,
        marginBottom: 0,
        padding: 0,
        marginLeft: 0, // align with menu
      }}
    >
      <Spin spinning={loadingWarehouses || loadingWarehouseDetails} tip="Loading..." size="large">
        <Spin spinning={loadingRacks} tip="Loading racks..." size="large">
          <Page
            inner
            style={{
              padding: 24,
              background: '#10162a',
              marginLeft: 0,
              minHeight: '80vh',
            }}
          >
          {/* Bin Details Modal */}
          {binModalOpen && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: '#000',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{
                background: '#181c2a',
                borderRadius: 0,
                width: '100vw',
                height: '100vh',
                overflow: 'auto',
                boxShadow: 'none',
                padding: 0,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}>
                <Spin spinning={loadingBinDetails} tip="Loading bin details..." size="large">
                  <div className="bin-modal-modalheader" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: '#181c2a', borderBottom: '2px solid #1976d2' }}>
                    <h2 style={{ flex: 1, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#ffe066' }}>Bin Details</h2>
                    <button
                      className="bin-modal-closebtn"
                      style={{
                        position: 'absolute',
                        top: 24,
                        right: 32,
                        width: 48,
                        height: 48,
                        background: '#181c2a',
                        color: '#ffe066',
                        border: '2px solid #ffe066',
                        borderRadius: '50%',
                        fontWeight: 900,
                        fontSize: '2rem',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px #0008',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}
                      onClick={() => setBinModalOpen(false)}
                      title="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="bin-modal-divider" />
                  <div className="bin-modal-table-container">
                    <style>{`
                      .bin-modal-table {
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 2px 12px #0001;
                        min-width: 900px;
                        background: #181c2a;
                      }
                      .bin-modal-table th {
                        position: sticky;
                        top: 0;
                        z-index: 2;
                        background: linear-gradient(90deg, #23263a 0%, #10162a 100%);
                        color: #ffe066;
                        font-weight: 800;
                        font-size: 17px;
                        padding: 14px 16px;
                        border-bottom: 2.5px solid #1976d2;
                        box-shadow: 0 2px 8px #1976d222;
                        letter-spacing: 0.5px;
                        text-transform: capitalize;
                        min-width: 120px;
                        max-width: 320px;
                        white-space: nowrap;
                      }
                      .bin-modal-table th.sticky-col, .bin-modal-table td.sticky-col {
                        position: sticky;
                        left: 0;
                        z-index: 3;
                        background: #23263a;
                        border-right: 2px solid #1976d2;
                      }
                      .bin-modal-table td {
                        padding: 12px 16px;
                        border-bottom: 1px solid #23263a;
                        color: #fff;
                        font-size: 15px;
                        background: #181c2a;
                        min-width: 120px;
                        max-width: 320px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        cursor: pointer;
                        transition: background 0.2s;
                      }
                      .bin-modal-table tr:nth-child(even) td {
                        background: #23263a;
                      }
                      .bin-modal-table tr:hover td {
                        background: #23263a;
                      }
                      .bin-modal-table-container {
                        width: 96vw;
                        max-width: 1800px;
                        margin: 0 auto;
                        overflow-x: auto;
                        background: #181c2a;
                        border-radius: 12px;
                        box-shadow: 0 2px 12px #0002;
                        padding: 24px;
                        margin-top: 0;
                      }
                    `}</style>
                    {tableRows.length > 0 ? (
                      <table className="bin-modal-table" style={{ width: '100%', borderCollapse: 'separate', marginBottom: 16 }}>
                        <thead>
                          <tr>
                            {displayKeys.map((key, idx) => (
                                <th
                                  key={key}
                                  className={idx === 0 ? 'sticky-col' : ''}
                                >
                                  {getBinDetailHeader(key)}
                                </th>
                              ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tableRows.map((row, i) => (
                            <tr key={i}>
                              {displayKeys.map((key, idx) => (
                                <td
                                  key={key}
                                  className={idx === 0 ? 'sticky-col' : ''}
                                  title={typeof row[key] === 'object' && row[key] !== null ? JSON.stringify(row[key]) : (row[key] ?? '')}
                                >
                                  {key === 'days'
                                    ? row[key]
                                    : typeof row[key] === 'number' && row[key] != null
                                      ? row[key].toLocaleString()
                                      : typeof row[key] === 'object' && row[key] !== null
                                        ? JSON.stringify(row[key])
                                        : row[key] != null && String(row[key]).length > 40
                                          ? String(row[key]).slice(0, 37) + '...'
                                          : row[key] ?? ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ color: '#d32f2f', fontWeight: 500 }}>No data</div>
                    )}
                    {/* Pagination */}
                    {binModalPagination && binModalPagination.totalPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                        <button
                          onClick={() => fetchBinDetails(currentBinId, binModalPage - 1, binModalPageSize)}
                          disabled={binModalPage === 1}
                          style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #ccc', background: binModalPage === 1 ? '#eee' : '#fff', color: '#333', cursor: binModalPage === 1 ? 'not-allowed' : 'pointer' }}
                        >Prev</button>
                        <span style={{ alignSelf: 'center', color: '#fff', fontWeight: 500 }}>Page {binModalPage} / {binModalPagination.totalPages}</span>
                        <button
                          onClick={() => fetchBinDetails(currentBinId, binModalPage + 1, binModalPageSize)}
                          disabled={binModalPage === binModalPagination.totalPages}
                          style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #ccc', background: binModalPage === binModalPagination.totalPages ? '#eee' : '#fff', color: '#333', cursor: binModalPage === binModalPagination.totalPages ? 'not-allowed' : 'pointer' }}
                        >Next</button>
                      </div>
                    )}
                  </div>
                </Spin>
              </div>
            </div>
          )}
          {/* Filters Card */}
          <div ref={filterPanelRef} style={{ background: '#23263a', borderRadius: 12, padding: 24, marginLeft: 20, marginBottom: 32, boxShadow: '0 2px 12px #0002', width: '100%' }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#ffe066', marginBottom: 16, letterSpacing: 1 }}>Filters</div>
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap', width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <label htmlFor="warehouse-select" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Warehouse</label>
                <select
                  value={pendingWarehouse}
                  onChange={e => {
                    setPendingWarehouse(e.target.value);
                    setPendingRack('');
                    setPendingBin('');
                  }}
                  style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
                >
                  <option value="">All Warehouses</option>
                  {loadingWarehouses && <option>Loading...</option>}
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <label htmlFor="rack-select" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Rack</label>
                <select
                  value={pendingRack}
                  onChange={e => {
                    setPendingRack(e.target.value);
                    setPendingBin('');
                  }}
                  style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
                >
                  <option value="">All Racks</option>
                  {racks.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <label htmlFor="bin-select" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Bin</label>
                <select
                  value={pendingBin}
                  onChange={e => setPendingBin(e.target.value)}
                  style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
                  disabled={!pendingRack}
                >
                  <option value="">All Bins</option>
                  {bins.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <label htmlFor="stock-category-select" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Stock Category</label>
                <select
                  id="stock-category-select"
                  value={pendingStockCategory}
                  onChange={e => setPendingStockCategory(e.target.value)}
                  style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
                >
                  <option value=''>All Categories</option>
                  {stockCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <label htmlFor="material-type-select" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Material</label>
                <AutoComplete
                  id="material-type-select"
                  value={
                    pendingMaterialType
                      ? (materialTypes.find(mat => mat.id === pendingMaterialType)?.name || '')
                      : ''
                  }
                  options={materialTypes.map(mat => ({ value: mat.id, label: mat.name }))}
                  onSelect={(value, option) => setPendingMaterialType(value)}
                  onChange={input => {
                    // If input matches a material name, set its id, else clear
                    const found = materialTypes.find(mat => mat.name === input);
                    if (found) setPendingMaterialType(found.id);
                    else if (input === '') setPendingMaterialType('');
                  }}
                  placeholder="All Materials"
                  style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
                  filterOption={(inputValue, option) =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  allowClear
                />
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 220, marginTop: 24 }}>
                <button onClick={handleSearch} style={{ padding: '10px 28px', borderRadius: 8, background: '#388e3c', color: '#fff', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: 16, minWidth: 100 }}>Search</button>
                <button onClick={handleClear} style={{ padding: '8px 20px', borderRadius: 8, background: '#444', color: '#fff', fontWeight: 500, border: 'none', cursor: 'pointer', fontSize: 14, minWidth: 100 }}>Clear Filters</button>
              </div>
            </div>
            {/* Active Filter Summary */}
            {(selectedWarehouse || selectedRack || selectedBin) && (
              <div style={{ marginTop: 18, color: '#ffe066', fontWeight: 500, fontSize: 15 }}>
                Showing: {activeWarehouse ? activeWarehouse.name : 'All Warehouses'}
                {selectedRack && <> / {activeRack ? activeRack.name : selectedRack}</>}
                {selectedBin && <> / {activeBin ? activeBin.name : selectedBin}</>}
              </div>
            )}
            {searchError && (
              <div style={{ color: '#d32f2f', fontWeight: 500, margin: '16px 0' }}>{searchError}</div>
            )}
          </div>
          {/* Racks and Pathways (use warehouseDetails if set, else filteredRacks) */}
          {(warehouseDetails && warehouseDetails.racks ? warehouseDetails.racks : filteredRacks).map((rack, i) => (
            <div
              key={rack.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: 32,
                marginLeft: 20,
                background: '#181c2a',
                borderRadius: 16,
                boxShadow: '0 4px 24px #000a',
                border: '1.5px solid #23263a',
                padding: '20px 0',
                position: 'relative',
                minHeight: 90,
                flexWrap: 'wrap',
                gap: 0,
              }}
            >
              {/* Rack label */}
              <div
                style={{
                  minWidth: 120,
                  color: '#ffe066',
                  fontWeight: 900,
                  fontSize: 22,
                  marginRight: 32,
                  background: '#23263a',
                  borderRadius: 12,
                  padding: '18px 28px',
                  textAlign: 'center',
                  letterSpacing: 2,
                  boxShadow: '0 2px 8px #0008',
                  border: '2px solid #23263a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textShadow: 'none',
                }}
              >
                <span style={{ marginRight: 8, fontSize: 22 }}>🗄️</span>
                {rack.name}
              </div>
              {/* Bins */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 16,
                  overflowX: 'visible',
                  flex: 1,
                  alignItems: 'center',
                  minHeight: 54,
                  rowGap: 16,
                  justifyContent: 'flex-start',
                }}
              >
                {(rack.stockBins || []).map(bin => (
                  <div
                    key={bin.id}
                    className="bin-2d"
                    style={{
                      minWidth: 80,
                      height: 60,
                      background: getBinColor(bin),
                      color: bin.availableStockQuantity === 0 ? '#888' : '#fff',
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #23263a',
                      boxShadow: '0 2px 8px #0008',
                      cursor: 'pointer',
                      marginRight: 0,
                      marginBottom: 0,
                      transition: 'box-shadow 0.2s, border-color 0.2s, background 0.2s',
                    }}
                    onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px #1976d255'}
                    onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 8px #0008'}
                    onClick={async () => {
                      setBinModalOpen(true);
                      setLoadingBinDetails(true);
                      await fetchBinDetails(bin.id, 1, binModalPageSize);
                    }}
                  >
                    <span>{bin.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.8 }}>
                      {typeof bin.availableStockQuantity === 'number' ? bin.availableStockQuantity.toLocaleString() : (bin.availableStockQuantity ?? '')}
                      {' / '}
                      {typeof bin.allowedStockQuantity === 'number' ? bin.allowedStockQuantity.toLocaleString() : (bin.allowedStockQuantity ?? '')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {/* Legend */}
          <div style={{ display: 'flex', gap: 24, marginLeft: 20, marginBottom: 16, alignItems: 'center' }}>
            {BIN_LEGEND.map(({ label, color }) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ background: color, width: 16, height: 16, display: 'inline-block', borderRadius: 4 }} />
                <span style={{ color: '#fff', borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, marginLeft: 2 }}>{label}</span>
              </span>
            ))}
          </div>
        </Page>
        </Spin>
      </Spin>
      {/* 2D/3D Toggle Button */}
      {onViewChange && (
        <button
          onClick={() => onViewChange('3d')}
          style={{
            position: 'absolute',
            top: 24,
            right: 24,
            zIndex: 10,
            padding: '10px 28px',
            borderRadius: 8,
            background: '#388e3c',
            color: '#fff',
            fontWeight: 700,
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            boxShadow: '0 2px 8px #388e3c44',
          }}
        >
          Switch to 3D View
        </button>
      )}
    </PageHeader>
  );
};

export default StoreView2D; 